'use client';

import React, { useState, useEffect } from 'react';
import { apiClient } from '@/app/api/client';
import { apiConfig } from '@/config/api';
import { Button } from '@/components/common/Button';
import { Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { createAuthenticatedBlobUrl } from '@/app/api/proxy';
// Types
interface ImageContext {
  articleTitle: string;
  currentParagraph: string;
  selectedText?: string;
  cursorPosition?: number;
  scenario?: 'hero' | 'contextual';
}

interface SelectedImage {
  url: string;
  alt: string;
  assetId?: string;
}

interface Asset {
  uuid: string;
  filename: string;
  description: string;
  contentType: string;
  size: number;
  url: string;
  tags: string[];
  metadata: any;
}

interface AssetMatch {
  asset: Asset;
  score: number;
  reasoning: string;
}

interface SuggestionsGridProps {
  context: ImageContext;
  articleId?: string;
  userMessage?: string;
  onSelect: (image: SelectedImage) => void;
  onNoMatches: () => void;
  onHasMatches: () => void;
  onSwitchToGenerate: () => void;
  selectedImageId?: string;
  onImageClick?: (image: SelectedImage) => void;
  mode?: 'match' | 'browse';
}

export const SuggestionsGrid: React.FC<SuggestionsGridProps> = ({
  context,
  userMessage,
  onSelect,
  onNoMatches,
  onHasMatches,
  onSwitchToGenerate,
  articleId,
  selectedImageId,
  onImageClick,
  mode = 'match',
}) => {
  const [matches, setMatches] = useState<AssetMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrls, setBlobUrls] = useState<Map<string, string>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadMatches = async () => {
      if (cancelled) return;
      await fetchMatches();
    };

    loadMatches();

    return () => {
      cancelled = true;
    };
  }, [context, articleId, userMessage]);

  // Lazy load current image when index changes
  useEffect(() => {
    if (matches.length === 0) return;

    const currentMatch = matches[currentIndex];
    if (!currentMatch) return;

    // Check if blob URL already loaded
    if (blobUrls.has(currentMatch.asset.uuid)) return;

    // Load blob URL for current image
    const loadCurrentImage = async () => {
      try {
        const blobUrl = await createAuthenticatedBlobUrl(currentMatch.asset.uuid);
        setBlobUrls(prev => new Map(prev).set(currentMatch.asset.uuid, blobUrl));
      } catch (err) {
        console.error(`Failed to load image ${currentMatch.asset.uuid}:`, err);
      }
    };

    loadCurrentImage();
  }, [currentIndex, matches]);

  const fetchMatches = async () => {
    // Prevent concurrent calls
    if (isLoading) {
      console.log('[SuggestionsGrid] Already loading, skipping duplicate call');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/assets/match', {
        articleId,
        context: {
          articleTitle: context.articleTitle,
          currentParagraph: context.currentParagraph,
          selectedText: context.selectedText,
          cursorPosition: context.cursorPosition,
          scenario: context.scenario || 'contextual',
        },
        userMessage: userMessage || undefined,
        limit: mode === 'browse' ? 24 : 6,
        mode,
      });

      const matchData = response.data.matches || [];
      setMatches(matchData);

      // Lazy loading: Only load the first image initially
      if (matchData.length > 0) {
        try {
          const firstBlobUrl = await createAuthenticatedBlobUrl(matchData[0].asset.uuid);
          setBlobUrls(new Map([[matchData[0].asset.uuid, firstBlobUrl]]));
        } catch (err) {
          console.error(`Failed to load first image:`, err);
        }
        onHasMatches();
      } else {
        onNoMatches();
      }
    } catch (err: any) {
      console.error('Error fetching asset matches:', err);
      setError(err.response?.data?.details || err.message || 'Failed to fetch suggestions');
      onNoMatches();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAsset = (match: AssetMatch) => {
    // Use the blob URL for the selected image (already authenticated and cached)
    const blobUrl = blobUrls.get(match.asset.uuid) || match.asset.url;
    const imageData = {
      url: blobUrl, // Use blob URL for authenticated access
      alt: match.asset.metadata?.alt || match.asset.filename,
      assetId: match.asset.uuid,
    };

    // If onImageClick is provided, use it for selection (two-step process)
    // Otherwise, use onSelect for immediate insertion (one-step process)
    if (onImageClick) {
      onImageClick(imageData);
    } else {
      onSelect(imageData);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 0.7) return 'bg-blue-100 text-blue-800 border-blue-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.9) return 'Excellent match';
    if (score >= 0.7) return 'Good match';
    return 'Possible match';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-sm text-gray-600">{mode === 'browse' ? 'Loading your assets...' : 'Finding matching images...'}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-red-600 mb-4">⚠️ Error loading suggestions</div>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <Button onClick={fetchMatches} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty state
  if (matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{mode === 'browse' ? 'No assets found' : 'No matching images found'}</h3>
        <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
          {mode === 'browse'
            ? "You don't have any uploaded assets yet."
            : "We couldn't find any existing images that match your article content. Try generating a new image instead."}
        </p>
        <Button onClick={onSwitchToGenerate} className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Generate New Image
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Matches carousel navigation handlers
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : matches.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < matches.length - 1 ? prev + 1 : 0));
  };

  const currentMatch = matches[currentIndex];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Image {currentIndex + 1} of {matches.length}
        </p>
        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            disabled={matches.length <= 1}
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
          </button>
          <button
            onClick={handleNext}
            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            disabled={matches.length <= 1}
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Single image display */}
      <div className="relative max-w-2xl mx-auto">
        <div
          className={`border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-lg ${selectedImageId === currentMatch.asset.uuid
            ? 'ring-4 ring-blue-500 shadow-xl'
            : currentMatch.score >= 0.9
              ? 'ring-2 ring-green-500'
              : ''
            }`}
          onClick={() => handleSelectAsset(currentMatch)}
        >
          {/* Image Preview */}
          <div className="relative aspect-video bg-gray-100">
            <img
              src={blobUrls.get(currentMatch.asset.uuid) || ''}
              alt={currentMatch.asset.description || currentMatch.asset.filename}
              className="w-full h-full object-cover"
            />
            {/* Score Badge */}
            {/* Score Badge - Hide in browse mode */}
            {mode !== 'browse' && (
              <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold border ${getScoreColor(currentMatch.score)}`}>
                {Math.round(currentMatch.score * 100)}% match
              </div>
            )}
            {/* Top Match Badge - Hide in browse mode */}
            {mode !== 'browse' && currentIndex === 0 && currentMatch.score >= 0.9 && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
                ⭐ Top Match
              </div>
            )}
          </div>

          {/* Asset Info */}
          <div className="p-3 space-y-2">
            <div className="text-sm font-medium text-gray-900 line-clamp-1">
              {currentMatch.asset.filename}
            </div>

            {/* AI Reasoning */}
            <div className="text-xs text-gray-600 line-clamp-3">
              {currentMatch.reasoning}
            </div>

            {/* Tags */}
            {currentMatch.asset.tags && currentMatch.asset.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {currentMatch.asset.tags.slice(0, 3).map((tag: string, idx: number) => (
                  <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                    {tag}
                  </span>
                ))}
                {currentMatch.asset.tags.length > 3 && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                    +{currentMatch.asset.tags.length - 3}
                  </span>
                )}
              </div>
            )}

            {/* Match Quality */}
            {/* Match Quality - Hide in browse mode */}
            {mode !== 'browse' && (
              <div className="text-xs text-gray-500">
                {getScoreLabel(currentMatch.score)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
